import {
  APIProduct,
  APIEndpointDefinition,
  DeveloperKey,
  OAuthClient,
  APIUsageLedgerEntry,
  WebhookEndpoint,
  WebhookDeliveryLog,
  EmbeddableWidgetConfig,
  PartnerIntegration,
  PublicDataRightsRecord,
  DeveloperIncident
} from '../services/developer/apiProductTypes';

export const DEVELOPER_EXECUTIVE_METRICS = {
  activeOrganizations: '1,420',
  activeProjects: '3,890',
  monthlyAPIRequests: '48,650,000',
  apiSuccessRatePct: 99.98,
  p95LatencyMs: 46,
  developerActivationRatePct: 88.4,
  timeToFirstSuccessMinutes: 2.8,
  activeProductionKeys: '2,640',
  activeWebhooks: '1,120',
  monthlyWidgetImpressions: '14,200,000',
  computeUnitsConsumedMonthly: '62,800,000',
  activePartners: '86',
  openIncidents: 0
};

export const API_PRODUCTS: APIProduct[] = [
  {
    id: 'prod-currency',
    name: 'Currency & FX Intelligence API',
    slug: 'currency-fx',
    category: 'CURRENCY',
    version: 'v1',
    stability: 'GENERAL_AVAILABILITY',
    description: 'Real-time mid-market rates, canonical reference rates, 170+ currencies, crypto and historical FX observations.',
    baseComputeUnits: 1,
    dataRightsClass: 'PUBLIC_REDISTRIBUTABLE',
    sourceAttribution: 'European Central Bank, Bank of England & Central Banks Open Data',
    endpointsCount: 4,
    activeDevelopers: 840,
    monthlyRequests: 18400000,
    p95LatencyMs: 28,
    errorRatePct: 0.01,
    status: 'ACTIVE'
  },
  {
    id: 'prod-salary',
    name: 'Salary, Paycheck & Tax Intelligence API',
    slug: 'salary-payroll-tax',
    category: 'SALARY',
    version: 'v1',
    stability: 'GENERAL_AVAILABILITY',
    description: 'Deterministic gross-to-net, net-to-gross, social contributions, and tax brackets across 35+ global jurisdictions.',
    baseComputeUnits: 3,
    dataRightsClass: 'PUBLIC_WITH_ATTRIBUTION',
    sourceAttribution: 'Official Tax Authorities (IRS, HMRC, DGFiP, CRA, ATO)',
    endpointsCount: 3,
    activeDevelopers: 512,
    monthlyRequests: 8950000,
    p95LatencyMs: 42,
    errorRatePct: 0.02,
    status: 'ACTIVE'
  },
  {
    id: 'prod-loan',
    name: 'Loan, Mortgage & Amortization API',
    slug: 'loan-mortgage',
    category: 'LOAN',
    version: 'v1',
    stability: 'GENERAL_AVAILABILITY',
    description: 'Actuarial amortization schedules, APR/APRC calculation, early prepayment optimization, and refinancing comparisons.',
    baseComputeUnits: 2,
    dataRightsClass: 'PUBLIC_REDISTRIBUTABLE',
    sourceAttribution: 'YamooPay Actuarial Financial Models',
    endpointsCount: 3,
    activeDevelopers: 420,
    monthlyRequests: 6420000,
    p95LatencyMs: 34,
    errorRatePct: 0.01,
    status: 'ACTIVE'
  },
  {
    id: 'prod-wealth',
    name: 'Compound Interest & FIRE Wealth API',
    slug: 'wealth-compound-fire',
    category: 'WEALTH',
    version: 'v1',
    stability: 'GENERAL_AVAILABILITY',
    description: 'Compound interest simulation, inflation-adjusted real returns, DCA cashflow modeling, and FIRE retirement projections.',
    baseComputeUnits: 2,
    dataRightsClass: 'PUBLIC_REDISTRIBUTABLE',
    sourceAttribution: 'YamooPay Wealth Engineering',
    endpointsCount: 3,
    activeDevelopers: 310,
    monthlyRequests: 4180000,
    p95LatencyMs: 31,
    errorRatePct: 0.01,
    status: 'ACTIVE'
  },
  {
    id: 'prod-universal-calc',
    name: 'Universal Everyday Math API',
    slug: 'universal-calculator',
    category: 'UNIVERSAL_CALCULATOR',
    version: 'v1',
    stability: 'GENERAL_AVAILABILITY',
    description: 'Sub-millisecond percentage, margin, markup, VAT/GST, tip & split, fuel, unit conversion, and date interval math.',
    baseComputeUnits: 1,
    dataRightsClass: 'PUBLIC_REDISTRIBUTABLE',
    sourceAttribution: 'YamooPay Deterministic Arithmetic Engine',
    endpointsCount: 6,
    activeDevelopers: 760,
    monthlyRequests: 9200000,
    p95LatencyMs: 14,
    errorRatePct: 0.005,
    status: 'ACTIVE'
  },
  {
    id: 'prod-transfer',
    name: 'Money Transfer Comparison API',
    slug: 'money-transfer',
    category: 'TRANSFER',
    version: 'v1',
    stability: 'BETA',
    description: 'Verified corridor fees, delivery speeds, and recipient estimates across verified licensed remittance providers.',
    baseComputeUnits: 5,
    dataRightsClass: 'PARTNER_RESTRICTED',
    sourceAttribution: 'YamooPay Verified Remittance Partners',
    endpointsCount: 2,
    activeDevelopers: 140,
    monthlyRequests: 1500000,
    p95LatencyMs: 95,
    errorRatePct: 0.05,
    status: 'ACTIVE'
  }
];

export const API_ENDPOINTS: APIEndpointDefinition[] = [
  {
    id: 'ep-fx-convert',
    productId: 'prod-currency',
    path: '/v1/currency/convert',
    method: 'POST',
    summary: 'Convert Currency with Verified Rates',
    description: 'Converts an amount from a base currency to a target currency using authoritative mid-market reference rates with strict decimal precision.',
    requiredScopes: ['currency.read', 'fx.convert'],
    computeUnits: 1,
    isIdempotent: true,
    rateLimitPerMinute: 600,
    requestSchema: {
      fromCurrency: 'USD',
      toCurrency: 'EUR',
      amount: '1000.00',
      rateType: 'MID_MARKET'
    },
    responseSchema: {
      baseCurrency: 'USD',
      quoteCurrency: 'EUR',
      exchangeRate: '0.9245',
      inputAmount: '1000.00',
      convertedAmount: '924.50',
      observedAt: '2026-08-25T09:30:00Z',
      rateType: 'MID_MARKET',
      freshness: 'LIVE',
      formulaVersion: 'v1.4.0'
    },
    sampleRequest: {
      fromCurrency: 'USD',
      toCurrency: 'EUR',
      amount: '5000.00'
    },
    sampleResponse: {
      baseCurrency: 'USD',
      quoteCurrency: 'EUR',
      exchangeRate: '0.9245',
      inputAmount: '5000.00',
      convertedAmount: '4622.50',
      observedAt: '2026-08-25T09:30:00Z',
      rateType: 'MID_MARKET',
      freshness: 'LIVE',
      provenance: 'European Central Bank Reference Stream',
      formulaVersion: 'v1.4.0'
    }
  },
  {
    id: 'ep-salary-calc',
    productId: 'prod-salary',
    path: '/v1/salary/calculate-paycheck',
    method: 'POST',
    summary: 'Gross-to-Net Paycheck Calculation',
    description: 'Calculates net paycheck, income tax deductions, social security, pension contributions, and employer cost across supported jurisdictions.',
    requiredScopes: ['salary.calculate'],
    computeUnits: 3,
    isIdempotent: true,
    rateLimitPerMinute: 300,
    requestSchema: {
      jurisdiction: 'US',
      state: 'CA',
      grossSalary: '120000.00',
      payPeriod: 'ANNUAL',
      filingStatus: 'SINGLE',
      taxYear: 2026
    },
    responseSchema: {
      jurisdiction: 'US',
      grossAmount: '120000.00',
      netAmount: '84950.00',
      effectiveTaxRatePct: 29.21,
      deductions: {
        federalIncomeTax: '18450.00',
        stateIncomeTax: '7420.00',
        socialSecurityFICA: '7440.00',
        medicare: '1740.00'
      },
      rulesetVersion: 'US_2026_V1',
      disclaimer: 'Deterministic statutory calculation. Does not constitute formal tax filing advice.'
    },
    sampleRequest: {
      jurisdiction: 'FR',
      grossSalary: '60000.00',
      payPeriod: 'ANNUAL',
      filingStatus: 'SINGLE',
      taxYear: 2026
    },
    sampleResponse: {
      jurisdiction: 'FR',
      grossAmount: '60000.00',
      netBeforeTax: '46800.00',
      netAfterIncomeTax: '41250.00',
      effectiveTaxRatePct: 31.25,
      employeeContributions: '13200.00',
      employerContributions: '25400.00',
      rulesetVersion: 'FR_2026_V1'
    }
  },
  {
    id: 'ep-loan-amortization',
    productId: 'prod-loan',
    path: '/v1/loans/amortization-schedule',
    method: 'POST',
    summary: 'Actuarial Loan Amortization Schedule',
    description: 'Generates detailed monthly amortization breakdown, principal/interest split, total borrowing cost, and APRC metrics.',
    requiredScopes: ['loan.calculate'],
    computeUnits: 2,
    isIdempotent: true,
    rateLimitPerMinute: 300,
    requestSchema: {
      principalAmount: '400000.00',
      annualInterestRatePct: '3.75',
      durationMonths: 300,
      originationFees: '2500.00'
    },
    responseSchema: {
      monthlyPayment: '2056.44',
      totalInterestPaid: '216932.00',
      totalCostOfCredit: '219432.00',
      effectiveAPRC: '3.82',
      schedulePreview: []
    },
    sampleRequest: {
      principalAmount: '350000.00',
      annualInterestRatePct: '4.10',
      durationMonths: 240
    },
    sampleResponse: {
      monthlyPayment: '2138.45',
      totalInterestPaid: '163228.00',
      totalPrincipalPaid: '350000.00',
      effectiveAPRC: '4.10',
      durationYears: 20
    }
  },
  {
    id: 'ep-wealth-compound',
    productId: 'prod-wealth',
    path: '/v1/wealth/compound-interest',
    method: 'POST',
    summary: 'Compound Interest & Future Value Simulation',
    description: 'Calculates future investment value, total principal deposits, accrued interest, and purchasing power adjusted for expected inflation.',
    requiredScopes: ['wealth.calculate'],
    computeUnits: 2,
    isIdempotent: true,
    rateLimitPerMinute: 400,
    requestSchema: {
      initialDeposit: '10000.00',
      monthlyContribution: '500.00',
      annualReturnRatePct: '8.00',
      years: 25,
      compoundFrequency: 'MONTHLY',
      expectedInflationPct: '2.50'
    },
    responseSchema: {
      nominalFutureValue: '475850.00',
      realInflationAdjustedValue: '256820.00',
      totalPrincipalInvested: '160000.00',
      totalAccruedInterest: '315850.00'
    },
    sampleRequest: {
      initialDeposit: '25000.00',
      monthlyContribution: '1000.00',
      annualReturnRatePct: '7.50',
      years: 20
    },
    sampleResponse: {
      nominalFutureValue: '668950.00',
      totalPrincipalInvested: '265000.00',
      totalAccruedInterest: '403950.00',
      growthMultiplier: '2.52x'
    }
  },
  {
    id: 'ep-universal-percentage',
    productId: 'prod-universal-calc',
    path: '/v1/calculators/percentage',
    method: 'POST',
    summary: 'Percentage, Margin & Markup Math',
    description: 'Sub-millisecond percentage calculations, percentage difference, percentage change, gross margin, and VAT extraction.',
    requiredScopes: ['calculator.calculate'],
    computeUnits: 1,
    isIdempotent: true,
    rateLimitPerMinute: 1200,
    requestSchema: {
      operation: 'PERCENTAGE_OF',
      valueA: '25.00',
      valueB: '400.00'
    },
    responseSchema: {
      result: '100.00',
      formula: '(25 / 100) * 400'
    },
    sampleRequest: {
      operation: 'MARGIN_TO_MARKUP',
      marginPct: '30.00'
    },
    sampleResponse: {
      marginPct: '30.00',
      markupPct: '42.86',
      formula: 'Margin / (1 - Margin)'
    }
  }
];

export const MOCK_DEVELOPER_KEYS: DeveloperKey[] = [
  {
    id: 'key-prod-01',
    name: 'Production Core Service (FinTech Gateway)',
    keyPrefix: 'yp_live_8f3a9e21b0',
    environment: 'PRODUCTION',
    organizationId: 'org-acme-fintech',
    projectId: 'proj-acme-core',
    scopes: ['currency.read', 'fx.convert', 'salary.calculate', 'loan.calculate', 'wealth.calculate'],
    createdAt: '2026-01-15T10:00:00Z',
    lastUsedAt: '2026-08-25T09:12:45Z',
    expiresAt: null,
    status: 'ACTIVE',
    rateLimitOverride: 1200
  },
  {
    id: 'key-test-01',
    name: 'Sandbox Integration Key',
    keyPrefix: 'yp_test_41c098df23',
    environment: 'SANDBOX',
    organizationId: 'org-acme-fintech',
    projectId: 'proj-acme-core',
    scopes: ['currency.read', 'fx.convert', 'salary.calculate', 'loan.calculate', 'wealth.calculate', 'calculator.calculate'],
    createdAt: '2026-01-15T10:05:00Z',
    lastUsedAt: '2026-08-25T09:28:10Z',
    expiresAt: null,
    status: 'ACTIVE'
  },
  {
    id: 'key-test-02',
    name: 'QA & Staging Automation Worker',
    keyPrefix: 'yp_test_92e8a155c4',
    environment: 'SANDBOX',
    organizationId: 'org-acme-fintech',
    projectId: 'proj-acme-staging',
    scopes: ['currency.read', 'fx.convert', 'calculator.calculate'],
    createdAt: '2026-04-10T14:30:00Z',
    lastUsedAt: '2026-08-24T18:40:00Z',
    expiresAt: '2026-12-31T23:59:59Z',
    status: 'ACTIVE'
  }
];

export const MOCK_OAUTH_CLIENTS: OAuthClient[] = [
  {
    id: 'oauth-client-01',
    clientId: 'yp_client_99a8b1c4',
    name: 'Acme Wealth Assistant Mobile App',
    clientType: 'PUBLIC',
    redirectUris: ['https://app.acmewealth.com/oauth/callback', 'acmewealth://auth-callback'],
    allowedScopes: ['currency.read', 'wealth.calculate', 'saved_calculations.read'],
    organizationId: 'org-acme-fintech',
    createdAt: '2026-03-01T11:00:00Z',
    status: 'ACTIVE',
    totalGrants: 4820
  },
  {
    id: 'oauth-client-02',
    clientId: 'yp_client_55e2d931',
    name: 'PayrollSync Enterprise Connector',
    clientType: 'CONFIDENTIAL',
    redirectUris: ['https://api.payrollsync.io/v1/auth/yamoo-callback'],
    allowedScopes: ['salary.calculate', 'tax_brackets.read'],
    organizationId: 'org-payrollsync',
    createdAt: '2026-05-12T09:20:00Z',
    status: 'ACTIVE',
    totalGrants: 1250
  }
];

export const MOCK_WEBHOOK_ENDPOINTS: WebhookEndpoint[] = [
  {
    id: 'wh-ep-01',
    url: 'https://api.acmewealth.com/webhooks/yamoopay',
    environment: 'PRODUCTION',
    subscribedEvents: ['fx.rate_alert.triggered', 'tax_rules.updated', 'usage.threshold_reached'],
    secretPreview: 'whsec_••••••••8a9f',
    status: 'ACTIVE',
    createdAt: '2026-02-10T08:00:00Z',
    lastDeliveryAt: '2026-08-25T08:45:12Z',
    successRatePct: 99.95,
    consecutiveFailures: 0
  },
  {
    id: 'wh-ep-02',
    url: 'https://staging-api.acmewealth.com/webhooks/test',
    environment: 'SANDBOX',
    subscribedEvents: ['fx.rate_alert.triggered', 'calculation.batch.completed'],
    secretPreview: 'whsec_••••••••11bc',
    status: 'ACTIVE',
    createdAt: '2026-03-15T15:20:00Z',
    lastDeliveryAt: '2026-08-25T09:10:00Z',
    successRatePct: 100.0,
    consecutiveFailures: 0
  }
];

export const MOCK_WEBHOOK_LOGS: WebhookDeliveryLog[] = [
  {
    id: 'del-log-901',
    endpointId: 'wh-ep-01',
    eventId: 'evt_fx_alert_8820',
    eventType: 'fx.rate_alert.triggered',
    payload: {
      alertId: 'alt_9921',
      pair: 'EUR/USD',
      triggerRate: '1.0850',
      observedRate: '1.0852',
      timestamp: '2026-08-25T08:45:12Z'
    },
    httpStatus: 200,
    durationMs: 48,
    timestamp: '2026-08-25T08:45:12Z',
    status: 'DELIVERED',
    attemptNumber: 1,
    signatureHeader: 't=1787647512,v1=9f8a7c6e5d4b3a210fedcba987654321'
  },
  {
    id: 'del-log-900',
    endpointId: 'wh-ep-01',
    eventId: 'evt_tax_2026_q3',
    eventType: 'tax_rules.updated',
    payload: {
      jurisdiction: 'UK',
      taxYear: 2026,
      changeType: 'THRESHOLD_UPDATE',
      effectiveDate: '2026-09-01'
    },
    httpStatus: 200,
    durationMs: 62,
    timestamp: '2026-08-24T14:10:05Z',
    status: 'DELIVERED',
    attemptNumber: 1,
    signatureHeader: 't=1787580605,v1=5d4b3a210fedcba9876543219f8a7c6e'
  }
];

export const MOCK_EMBEDDABLE_WIDGETS: EmbeddableWidgetConfig[] = [
  {
    id: 'wdg-fx-01',
    widgetType: 'CURRENCY_CONVERTER',
    name: 'Global Currency Converter Widget',
    allowedDomains: ['acmefinance.com', 'travelportal.net', 'investblog.io'],
    defaultCurrency: 'USD',
    primaryColor: '#1E40AF',
    brandingMode: 'POWERED_BY_YAMOOPAY',
    iframeSnippet: '<iframe src="https://widgets.yamoopay.com/v1/currency?id=wdg-fx-01&theme=light" width="100%" height="420" frameborder="0" sandbox="allow-scripts allow-same-origin" title="YamooPay Currency Converter"></iframe>',
    monthlyLoads: 4850000,
    status: 'ACTIVE',
    createdAt: '2026-01-20T12:00:00Z'
  },
  {
    id: 'wdg-mortgage-01',
    widgetType: 'MORTGAGE_CALCULATOR',
    name: 'Real Estate Mortgage & Amortization Calculator',
    allowedDomains: ['luxuryhomes.com', 'realtygroup.org'],
    defaultCurrency: 'EUR',
    primaryColor: '#059669',
    brandingMode: 'POWERED_BY_YAMOOPAY',
    iframeSnippet: '<iframe src="https://widgets.yamoopay.com/v1/mortgage?id=wdg-mortgage-01&theme=light" width="100%" height="540" frameborder="0" sandbox="allow-scripts allow-same-origin" title="YamooPay Mortgage Calculator"></iframe>',
    monthlyLoads: 3200000,
    status: 'ACTIVE',
    createdAt: '2026-02-14T09:30:00Z'
  },
  {
    id: 'wdg-compound-01',
    widgetType: 'COMPOUND_INTEREST',
    name: 'FIRE & Wealth Compound Interest Visualizer',
    allowedDomains: ['firecommunity.com', 'moneyblog.co'],
    defaultCurrency: 'USD',
    primaryColor: '#1E40AF',
    brandingMode: 'POWERED_BY_YAMOOPAY',
    iframeSnippet: '<iframe src="https://widgets.yamoopay.com/v1/compound?id=wdg-compound-01&theme=light" width="100%" height="480" frameborder="0" sandbox="allow-scripts allow-same-origin" title="YamooPay Wealth Calculator"></iframe>',
    monthlyLoads: 2400000,
    status: 'ACTIVE',
    createdAt: '2026-03-05T14:15:00Z'
  }
];

export const MOCK_PARTNERS: PartnerIntegration[] = [
  {
    id: 'part-01',
    partnerName: 'Acme Global FinTech Corp',
    category: 'FINTECH',
    integrationMode: 'REST_API',
    planTier: 'ENTERPRISE',
    dataSharingScope: ['FX_LIVE_RATES', 'SALARY_CALCULATOR', 'LOAN_SCHEDULES'],
    contractReference: 'CTR-2026-ACME-001',
    securityReviewStatus: 'APPROVED',
    monthlyQuotaComputeUnits: 25000000,
    usedComputeUnits: 14200000,
    status: 'ACTIVE'
  },
  {
    id: 'part-02',
    partnerName: 'Nordic Bank & Wealth Group',
    category: 'BANK',
    integrationMode: 'REST_API',
    planTier: 'ENTERPRISE',
    dataSharingScope: ['FX_LIVE_RATES', 'WEALTH_COMPOUND'],
    contractReference: 'CTR-2026-NBW-094',
    securityReviewStatus: 'APPROVED',
    monthlyQuotaComputeUnits: 10000000,
    usedComputeUnits: 6800000,
    status: 'ACTIVE'
  },
  {
    id: 'part-03',
    partnerName: 'Global Real Estate Portal',
    category: 'REAL_ESTATE',
    integrationMode: 'EMBEDDABLE_WIDGET',
    planTier: 'GROWTH',
    dataSharingScope: ['LOAN_SCHEDULES'],
    contractReference: 'CTR-2026-GREP-112',
    securityReviewStatus: 'APPROVED',
    monthlyQuotaComputeUnits: 5000000,
    usedComputeUnits: 3200000,
    status: 'ACTIVE'
  }
];

export const MOCK_PUBLIC_DATA_RIGHTS: PublicDataRightsRecord[] = [
  {
    id: 'dr-01',
    datasetName: 'European Central Bank FX Reference Rates',
    primarySource: 'ECB Open Data Dissemination Service',
    redistributionClass: 'PUBLIC_REDISTRIBUTABLE',
    attributionRequired: true,
    attributionNotice: 'Source: European Central Bank reference exchange rates under European Union Open Data License.',
    commercialUseAllowed: true,
    licenseContractRef: 'ECB-OD-2026',
    verifiedAt: '2026-01-10T10:00:00Z',
    reviewer: 'Chief Data Governance Officer',
    status: 'APPROVED'
  },
  {
    id: 'dr-02',
    datasetName: 'US IRS & State Statutory Tax Withholding Formulas',
    primarySource: 'Internal Revenue Service Publication 15-T',
    redistributionClass: 'PUBLIC_REDISTRIBUTABLE',
    attributionRequired: true,
    attributionNotice: 'Statutory mathematical formulas compiled from public domain IRS statutes.',
    commercialUseAllowed: true,
    licenseContractRef: 'IRS-PUB-15T-2026',
    verifiedAt: '2026-01-12T14:00:00Z',
    reviewer: 'Principal Tax Counsel',
    status: 'APPROVED'
  },
  {
    id: 'dr-03',
    datasetName: 'Remittance Corridor Partner Live Quotes',
    primarySource: 'Licensed Money Remittance Providers',
    redistributionClass: 'PARTNER_RESTRICTED',
    attributionRequired: true,
    attributionNotice: 'Display permissible solely to end-users via YamooPay quote aggregator.',
    commercialUseAllowed: false,
    licenseContractRef: 'PARTNER-RESTRICTED-COMMERCIAL-CLAUSE-4',
    verifiedAt: '2026-02-01T09:00:00Z',
    reviewer: 'Head of FinTech Partnerships',
    status: 'RESTRICTED'
  }
];

export const MOCK_DEVELOPER_INCIDENTS: DeveloperIncident[] = [
  {
    id: 'inc-dev-01',
    type: 'RATE_LIMIT_FAILURE',
    severity: 'MODERATE_P2',
    description: 'Redis rate limiter cluster failover caused 120 requests to receive 429 throttling intermittently.',
    affectedProduct: 'Universal Calculator API',
    status: 'RESOLVED',
    detectedAt: '2026-08-20T14:22:00Z',
    mitigationAction: 'Enhanced Redis sentinel retry policy and client-side failover backoff.'
  },
  {
    id: 'inc-dev-02',
    type: 'DATA_RIGHTS_VIOLATION',
    severity: 'CRITICAL_P0',
    description: 'Guaranteed pre-launch block on unapproved third-party commercial FX reseller feed.',
    affectedProduct: 'Currency & FX Intelligence API',
    status: 'RESOLVED',
    detectedAt: '2026-08-15T09:00:00Z',
    mitigationAction: 'Enforced automated CI gate rejecting any endpoint lacking verified PUBLIC_REDISTRIBUTABLE metadata.'
  }
];

// Helper code snippet generator for Quickstart
export function generateCodeSnippet(language: 'CURL' | 'TYPESCRIPT' | 'PYTHON', apiKey: string = 'yp_test_your_sandbox_key_here') {
  if (language === 'CURL') {
    return `curl -X POST https://api.yamoopay.com/v1/currency/convert \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "fromCurrency": "USD",
    "toCurrency": "EUR",
    "amount": "1000.00"
  }'`;
  }

  if (language === 'TYPESCRIPT') {
    return `// npm install @yamoopay/sdk
import { YamooPayClient } from '@yamoopay/sdk';

const yamoopay = new YamooPayClient({
  apiKey: '${apiKey}',
  environment: 'sandbox' // or 'production'
});

async function main() {
  const result = await yamoopay.currency.convert({
    fromCurrency: 'USD',
    toCurrency: 'EUR',
    amount: '1000.00'
  });

  console.log(\`Converted Amount: \${result.convertedAmount} \${result.quoteCurrency}\`);
  console.log(\`Rate: \${result.exchangeRate} (Observed at: \${result.observedAt})\`);
}

main();`;
  }

  return `# pip install yamoopay-python
from yamoopay import YamooPayClient

client = YamooPayClient(
    api_key="${apiKey}",
    environment="sandbox"
)

response = client.currency.convert(
    from_currency="USD",
    to_currency="EUR",
    amount="1000.00"
)

print(f"Converted: {response.converted_amount} {response.quote_currency}")
print(f"Exchange Rate: {response.exchange_rate}")`;
}
